import axios from 'axios';
import { Cctv, CctvUpdate, CctvCreate } from '../types/cctv'; // CCTV 관련 타입
import { ApiResponse } from '../types/api'; // ApiResponse 타입
import { getPoolIdByCode } from '../utils/poolUtils';
import { poolApi } from './poolApi';
import { API_ORIGIN } from '../config';

// API 기본 URL 설정
const API_URL = API_ORIGIN;

/**
 * API 응답 처리 표준 함수
 */
const handleApiResponse = <T>(response: any): ApiResponse<T> => {
    if (response.data && typeof response.data.success === 'boolean') {
        return {
            success: response.data.success,
            data: response.data.data as T,
            message: response.data.message || (response.data.success ? '요청 성공' : '요청 실패'),
            status: response.status
        };
    } else {
        return {
            success: response.status >= 200 && response.status < 300,
            data: response.data as T,
            message: response.status >= 200 && response.status < 300 ? '요청 성공' : `요청 실패 (상태 ${response.status})`,
            status: response.status
        };
    }
};

/**
 * API 오류 처리 표준 함수
 */
const handleApiError = <T>(error: any): ApiResponse<T> => {
    console.error('API 처리 오류:', error);
    const status = error.response?.status || 500;
    
    let message = '알 수 없는 오류';
    if (error.response?.data?.detail) {
        message = error.response.data.detail;
    } else if (error.response?.data?.message) {
        message = error.response.data.message;
    } else if (error.message) {
        message = error.message;
    }
    
    return {
        success: false,
        data: null,
        message,
        status,
        error: error
    };
};

/**
 * CCTV API 모듈
 * 모든 주변기기(CCTV 등)는 pool_id 기반으로만 동작하도록 수정
 */
export const cctvApi = {
    /**
     * 시설 코드로 CCTV 목록 조회 (새로운 API)
     * 수정된 방식: `/api/pools/code/{pool_code}/cctvs` 엔드포인트 사용
     */
    getCctvsByPoolCode: async (poolCode: string): Promise<ApiResponse<Cctv[]>> => {
        try {
            console.log(`[cctvApi] 엔드포인트 호출: /api/pools/code/${poolCode}/cctvs`);
            const response = await axios.get(`${API_URL}/api/pools/code/${poolCode}/cctvs`);
            console.log(`[cctvApi] 응답:`, response.data);
            
            return handleApiResponse<Cctv[]>(response);
        } catch (error) {
            console.error(`[cctvApi] 시설 코드(${poolCode})의 CCTV 목록 조회 오류:`, error);
            return handleApiError<Cctv[]>(error);
        }
    },
    
    /**
     * 시설 ID로 CCTV 목록 조회
     */
    getCctvsByPoolId: async (poolId: number | string): Promise<ApiResponse<Cctv[]>> => {
        try {
            const numericPoolId = typeof poolId === 'string' ? parseInt(poolId) : poolId;
            
            if (isNaN(numericPoolId)) {
                throw new Error(`유효하지 않은 시설 ID: ${poolId}`);
            }
            
            const response = await axios.get(`${API_URL}/api/equipment/${numericPoolId}/cctvs`);
            console.log(`[cctvApi] 시설 ID(${numericPoolId})의 CCTV 목록 조회 응답:`, response.data);
            return handleApiResponse<Cctv[]>(response);
        } catch (error) {
            console.error(`[cctvApi] 시설 ID(${poolId})의 CCTV 목록 조회 오류:`, error);
            return handleApiError<Cctv[]>(error);
        }
    },
    
    /**
     * 특정 CCTV 조회
     */
    getCctvById: async (poolId: number | string, cctvId: number | string): Promise<ApiResponse<Cctv>> => {
        try {
            console.log(`[cctvApi] 시설 ID(${poolId})의 CCTV ID(${cctvId}) 조회 시작`);
            
            const response = await axios.get(`${API_URL}/api/equipment/${poolId}/cctvs/${cctvId}`);
            console.log(`[cctvApi] CCTV ID(${cctvId}) 조회 응답:`, response.data);
            
            return handleApiResponse<Cctv>(response);
        } catch (error) {
            console.error(`[cctvApi] CCTV ID(${cctvId}) 조회 오류:`, error);
            return handleApiError<Cctv>(error);
        }
    },
    
    /**
     * CCTV 업데이트 
     */
    updateCctv: async (
        poolId: number | string,
        cctvId: number | string,
        cctvData: Partial<Cctv>
    ): Promise<ApiResponse<Cctv>> => {
        try {
            console.log(`[cctvApi] 시설 ID(${poolId})의 CCTV ID(${cctvId}) 업데이트 시작`);
            console.log(`[cctvApi] CCTV 업데이트 원본 데이터:`, JSON.stringify(cctvData, null, 2));
            
            const numericPoolId = typeof poolId === 'string' ? parseInt(poolId) : poolId;
            const numericCctvId = typeof cctvId === 'string' ? parseInt(cctvId) : cctvId;
            
            if (isNaN(numericPoolId) || isNaN(numericCctvId)) {
                throw new Error('유효하지 않은 시설 ID 또는 CCTV ID 형식');
            }
            
            // 백엔드에 전송할 최종 업데이트 데이터 - CctvUpdate 인터페이스에 맞게 구성
            const finalData: Partial<Cctv> = {
                name: cctvData.name,
                code: cctvData.code,
                status: cctvData.status,
                location: cctvData.location,
                stream_url: cctvData.stream_url,
                is_active: cctvData.is_active,
                last_maintenance: cctvData.last_maintenance === '' ? null : cctvData.last_maintenance,
                next_maintenance: cctvData.next_maintenance === '' ? null : cctvData.next_maintenance,
            };
            
            // 로깅용 데이터에서 undefined/null 값 제거
            const loggingData = { ...finalData };
            Object.keys(loggingData).forEach(key => {
                if (loggingData[key as keyof Partial<Cctv>] === undefined || loggingData[key as keyof Partial<Cctv>] === null) {
                    delete loggingData[key as keyof Partial<Cctv>];
                }
            });
            
            console.log(`[cctvApi] CCTV 최종 업데이트 데이터(로깅용):`, JSON.stringify(loggingData, null, 2));
            
            // API 호출 시 숫자 ID 사용
            const apiUrl = `/api/equipment/${numericPoolId}/cctvs/${numericCctvId}`;
            console.log(`[cctvApi] PUT ${apiUrl} 요청`);
            
            // PUT 메서드 사용하여 업데이트
            const response = await axios.put(`${API_URL}${apiUrl}`, finalData);
            console.log(`[cctvApi] CCTV ID(${cctvId}) 업데이트 응답:`, response.data);
            
            return handleApiResponse<Cctv>(response);
        } catch (error) {
            console.error(`[cctvApi] CCTV ID(${cctvId}) 업데이트 오류:`, error);
            
            // 더 자세한 오류 정보 로깅
            if (error.response) {
                console.error('[cctvApi] 응답 데이터:', error.response.data);
                console.error('[cctvApi] 응답 상태:', error.response.status);
            } else if (error.request) {
                console.error('[cctvApi] 요청 정보:', error.request);
            } else {
                console.error('[cctvApi] 오류 메시지:', error.message);
            }
            
            return handleApiError<Cctv>(error);
        }
    },
    
    /**
     * CCTV 삭제
     */
    deleteCctv: async (poolId: number | string, cctvId: number | string): Promise<ApiResponse<void>> => {
        try {
            console.log(`[cctvApi] 시설 ID(${poolId})의 CCTV ID(${cctvId}) 삭제 시작`);
            
            const numericPoolId = typeof poolId === 'string' ? parseInt(poolId) : poolId;
            const numericCctvId = typeof cctvId === 'string' ? parseInt(cctvId) : cctvId;
            
            if (isNaN(numericPoolId) || isNaN(numericCctvId)) {
                throw new Error('유효하지 않은 시설 ID 또는 CCTV ID 형식');
            }
            
            const response = await axios.delete(`${API_URL}/api/equipment/${numericPoolId}/cctvs/${numericCctvId}`);
            console.log(`[cctvApi] CCTV ID(${cctvId}) 삭제 응답:`, response);
            
            return handleApiResponse<void>(response);
        } catch (error) {
            console.error(`[cctvApi] CCTV ID(${cctvId}) 삭제 오류:`, error);
            return handleApiError<void>(error);
        }
    },
    
    /**
     * 새 CCTV 생성
     */
    createCctv: async (poolId: number | string, cctvData: Partial<CctvCreate>): Promise<ApiResponse<Cctv>> => {
        try {
            console.log(`[cctvApi] 시설 ID(${poolId})에 새 CCTV 생성 시작`);
            console.log(`[cctvApi] CCTV 생성 원본 데이터:`, JSON.stringify(cctvData, null, 2));
            
            const numericPoolId = Number(poolId);
            if (isNaN(numericPoolId)) {
                throw new Error('유효하지 않은 시설 ID 형식');
            }
            
            // 백엔드에 전송할 최종 데이터 준비 - CctvCreate 인터페이스에 맞게 구성
            const finalData: Partial<CctvCreate> = {
                pool_id: numericPoolId,
                name: cctvData.name,
                location: cctvData.location,
                
                // 선택적 필드
                code: cctvData.code === '' ? `CCTV-${numericPoolId}-${Date.now().toString().slice(-6)}` : cctvData.code,
                status: cctvData.status || 'ACTIVE',
                stream_url: cctvData.stream_url,
                is_active: cctvData.is_active !== undefined ? cctvData.is_active : true,
                purpose: cctvData.purpose,
                last_maintenance: cctvData.last_maintenance === '' ? null : cctvData.last_maintenance,
                next_maintenance: cctvData.next_maintenance === '' ? null : cctvData.next_maintenance,
                camera_type: cctvData.camera_type,
                thumbnail_url: cctvData.thumbnail_url,
                has_alert: cctvData.has_alert
            };
            
            // 로깅용 데이터에서 undefined/null 값 제거
            const loggingData = { ...finalData };
            Object.keys(loggingData).forEach(key => {
                if (loggingData[key as keyof Partial<CctvCreate>] === undefined || loggingData[key as keyof Partial<CctvCreate>] === null) {
                    delete loggingData[key as keyof Partial<CctvCreate>];
                }
            });
            
            console.log(`[cctvApi] CCTV 최종 생성 데이터:`, JSON.stringify(loggingData, null, 2));
            console.log(`[cctvApi] 백엔드 API 엔드포인트: ${API_URL}/api/equipment/${numericPoolId}/cctvs`);
            
            // API 호출
            const response = await axios.post(`${API_URL}/api/equipment/${numericPoolId}/cctvs`, finalData);
            console.log('[cctvApi] CCTV 생성 응답:', response.data);
            
            return handleApiResponse<Cctv>(response);
        } catch (error) {
            console.error(`[cctvApi] 새 CCTV 생성 오류:`, error);
            return handleApiError<Cctv>(error);
        }
    }
}; 